import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import '../App.css';

interface CatalogPackageType {
  name: string;
  volume: number;
  enabled: number;
  weightLbs?: number | null;
}

const emptyForm = { name: '', volume: '', weightLbs: '', enabled: true };

const PackageTypes: React.FC = () => {
  const [types, setTypes] = useState<CatalogPackageType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editName, setEditName] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', volume: '', weightLbs: '', enabled: true });

  const fetchTypes = useCallback(async () => {
    try {
      const res = await apiFetch('/api/package-types?all=1');
      const data = await res.json();
      setTypes(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load package types';
      setError(message);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const parseOptionalWeight = (raw: string): number | null | undefined => {
    if (raw === '') return null;
    const w = parseFloat(raw);
    if (!Number.isFinite(w) || w < 0) return undefined;
    return w;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSuccess(null);
    const volume = parseFloat(form.volume);
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!Number.isFinite(volume) || volume <= 0) {
      setError('Volume must be a positive number (barrels)');
      return;
    }
    const weightLbs = parseOptionalWeight(form.weightLbs);
    if (weightLbs === undefined) {
      setError('Weight must be a non-negative number (lbs)');
      return;
    }
    try {
      setSaving(true);
      await apiFetch('/api/package-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          volume,
          enabled: form.enabled,
          weightLbs,
        }),
      });
      setForm(emptyForm);
      setSuccess(`Added "${form.name.trim()}"`);
      await fetchTypes();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add package type';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (pkg: CatalogPackageType) => {
    setEditName(pkg.name);
    setEditForm({
      name: pkg.name,
      volume: String(pkg.volume),
      weightLbs: pkg.weightLbs != null && Number.isFinite(Number(pkg.weightLbs)) ? String(pkg.weightLbs) : '',
      enabled: !!Number(pkg.enabled),
    });
    setError(null);
    setSuccess(null);
  };

  const handleSaveEdit = async () => {
    if (!editName || saving) return;
    setError(null);
    setSuccess(null);
    const volume = parseFloat(editForm.volume);
    if (!editForm.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!Number.isFinite(volume) || volume <= 0) {
      setError('Volume must be a positive number (barrels)');
      return;
    }
    const weightLbs = parseOptionalWeight(editForm.weightLbs);
    if (weightLbs === undefined) {
      setError('Weight must be a non-negative number (lbs)');
      return;
    }
    try {
      setSaving(true);
      await apiFetch(`/api/package-types/${encodeURIComponent(editName)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editForm.name.trim(),
          volume,
          enabled: editForm.enabled,
          weightLbs,
        }),
      });
      setEditName(null);
      setSuccess('Package type updated');
      await fetchTypes();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update package type';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (pkg: CatalogPackageType) => {
    setError(null);
    setSuccess(null);
    try {
      await apiFetch(`/api/package-types/${encodeURIComponent(pkg.name)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !Number(pkg.enabled) }),
      });
      await fetchTypes();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update package type';
      setError(message);
    }
  };

  return (
    <div className="page-container container">
      <h2 className="app-header mb-4">Package Types</h2>
      <p className="mb-3" style={{ color: '#FFFFFF' }}>
        Catalog of packaging sizes used in product and batch dropdowns. Volume is in beer barrels (bbl).
        Weight (lbs) is used on the Bill of Lading.
      </p>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form className="mb-4" onSubmit={handleCreate}>
        <h3 className="app-header mb-3" style={{ fontSize: '1.1rem' }}>Add package type</h3>
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Name</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Case - 4x6 - 12oz - Can"
              required
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Volume (bbl)</label>
            <input
              className="form-control"
              type="number"
              step="0.00001"
              min="0.00001"
              value={form.volume}
              onChange={(e) => setForm({ ...form, volume: e.target.value })}
              placeholder="0.07272"
              required
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Weight (lbs)</label>
            <input
              className="form-control"
              type="number"
              step="0.01"
              min="0"
              value={form.weightLbs}
              onChange={(e) => setForm({ ...form, weightLbs: e.target.value })}
              placeholder="18"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label d-block">Enabled</label>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary w-100" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      </form>

      <div className="inventory-table-container">
        {types.length === 0 ? (
          <div className="alert alert-info">No package types found.</div>
        ) : (
          <table className="inventory-table table table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Volume (bbl)</th>
                <th>Gallons</th>
                <th>Weight (lbs)</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((pkg) => (
                <tr key={pkg.name}>
                  <td>
                    {editName === pkg.name ? (
                      <input
                        className="form-control"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    ) : (
                      pkg.name
                    )}
                  </td>
                  <td>
                    {editName === pkg.name ? (
                      <input
                        className="form-control"
                        type="number"
                        step="0.00001"
                        min="0.00001"
                        value={editForm.volume}
                        onChange={(e) => setEditForm({ ...editForm, volume: e.target.value })}
                      />
                    ) : (
                      Number(pkg.volume).toFixed(5)
                    )}
                  </td>
                  <td>{(Number(pkg.volume) * 31).toFixed(2)}</td>
                  <td>
                    {editName === pkg.name ? (
                      <input
                        className="form-control"
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.weightLbs}
                        onChange={(e) => setEditForm({ ...editForm, weightLbs: e.target.value })}
                      />
                    ) : pkg.weightLbs != null && Number.isFinite(Number(pkg.weightLbs)) ? (
                      Number(pkg.weightLbs).toFixed(2)
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {editName === pkg.name ? (
                      <input
                        type="checkbox"
                        checked={editForm.enabled}
                        onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                      />
                    ) : Number(pkg.enabled) ? (
                      'Yes'
                    ) : (
                      'No'
                    )}
                  </td>
                  <td>
                    {editName === pkg.name ? (
                      <>
                        <button className="btn btn-primary btn-sm me-2" type="button" onClick={handleSaveEdit} disabled={saving}>
                          Save
                        </button>
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => setEditName(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-primary btn-sm me-2" type="button" onClick={() => startEdit(pkg)}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" type="button" onClick={() => handleToggleEnabled(pkg)}>
                          {Number(pkg.enabled) ? 'Disable' : 'Enable'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PackageTypes;
